import { Plus } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';

export interface ProductsEmptyStateProps {
  /** Called when the merchant taps the "Add product" CTA. The parent
   *  route wires this to `navigate('/products/new')` (design D4). */
  readonly onAddProduct: () => void;
}

/**
 * Empty-state panel for the products list.
 *
 * Rendered when `useProducts` returns an empty array. The warm shelf
 * illustration mirrors the fallback HTML in
 * `.dukanti/.stitch/designs/products-empty-state.html` — an olive-branch
 * wooden shelf with a woven basket, rendered inline so we don't have to
 * ship an extra SVG asset (tokens tie to the Tailwind theme so it
 * reflows under dark mode / theme tweaks via Tailwind overrides).
 */
export function ProductsEmptyState({ onAddProduct }: ProductsEmptyStateProps) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="products-empty-state"
      className="flex min-h-[calc(100vh-14rem)] items-center justify-center px-5 py-10"
    >
      <div className="border-border bg-card shadow-admin-card max-w-sm rounded-lg border p-8 text-center">
        <svg
          viewBox="0 0 280 220"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          className="mx-auto mb-8 h-[220px] w-[280px]"
        >
          {/* Background warm glow */}
          <ellipse cx="140" cy="180" rx="100" ry="20" fill="#E8E1D2" opacity="0.4" />

          {/* Olive branches left */}
          <path
            d="M30 60 Q20 80 35 100 Q25 120 40 140"
            stroke="#A3B26E"
            strokeWidth="2"
            fill="none"
          />
          <ellipse
            cx="22"
            cy="85"
            rx="8"
            ry="5"
            fill="#A3B26E"
            opacity="0.7"
            transform="rotate(-30 22 85)"
          />
          <ellipse
            cx="38"
            cy="110"
            rx="8"
            ry="5"
            fill="#7A8B4A"
            opacity="0.6"
            transform="rotate(20 38 110)"
          />
          <ellipse
            cx="28"
            cy="130"
            rx="7"
            ry="4"
            fill="#A3B26E"
            opacity="0.7"
            transform="rotate(-15 28 130)"
          />

          {/* Olive branches right */}
          <path
            d="M250 60 Q260 80 245 100 Q255 120 240 140"
            stroke="#A3B26E"
            strokeWidth="2"
            fill="none"
          />
          <ellipse
            cx="258"
            cy="85"
            rx="8"
            ry="5"
            fill="#A3B26E"
            opacity="0.7"
            transform="rotate(30 258 85)"
          />
          <ellipse
            cx="242"
            cy="110"
            rx="8"
            ry="5"
            fill="#7A8B4A"
            opacity="0.6"
            transform="rotate(-20 242 110)"
          />
          <ellipse
            cx="252"
            cy="130"
            rx="7"
            ry="4"
            fill="#A3B26E"
            opacity="0.7"
            transform="rotate(15 252 130)"
          />

          {/* Shelf board */}
          <rect x="55" y="148" width="170" height="12" rx="3" fill="#D4CAB5" />
          <rect x="55" y="148" width="170" height="4" rx="2" fill="#C4BAA5" />

          {/* Shelf brackets */}
          <rect x="75" y="148" width="6" height="30" rx="2" fill="#C4BAA5" />
          <rect x="199" y="148" width="6" height="30" rx="2" fill="#C4BAA5" />

          {/* Woven basket on shelf */}
          <ellipse cx="140" cy="148" rx="28" ry="8" fill="#C4BAA5" />
          <path d="M112 148 Q112 122 140 118 Q168 122 168 148" fill="#D4CAB5" />
          {/* Basket weave lines */}
          <path d="M118 138 Q140 134 162 138" stroke="#B8AE9A" strokeWidth="1.2" fill="none" />
          <path d="M115 143 Q140 139 165 143" stroke="#B8AE9A" strokeWidth="1.2" fill="none" />
          <path d="M126 127 L126 148" stroke="#B8AE9A" strokeWidth="1" opacity="0.6" />
          <path d="M140 124 L140 148" stroke="#B8AE9A" strokeWidth="1" opacity="0.6" />
          <path d="M154 127 L154 148" stroke="#B8AE9A" strokeWidth="1" opacity="0.6" />

          {/* Basket handle */}
          <path
            d="M126 126 Q140 112 154 126"
            stroke="#C4BAA5"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />

          {/* Small olive accents in basket */}
          <circle cx="138" cy="136" r="4" fill="#7A8B4A" opacity="0.4" />
          <circle cx="145" cy="133" r="3" fill="#A3B26E" opacity="0.4" />
        </svg>

        <h2
          className="font-display text-foreground mb-2 text-2xl font-bold leading-snug"
          data-testid="products-empty-state-title"
        >
          {t('admin.products.empty.title')}
        </h2>
        <p
          className="text-muted-foreground mx-auto mb-5 max-w-xs text-sm leading-relaxed"
          data-testid="products-empty-state-subtitle"
        >
          {t('admin.products.empty.subtitle')}
        </p>
        <Button
          type="button"
          onClick={onAddProduct}
          data-testid="products-empty-state-cta"
          className="px-7"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          {t('admin.products.empty.cta')}
        </Button>
      </div>
    </div>
  );
}
